import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { workflowsApi } from '@/api/workflows';
import type { WorkflowInstance, WorkflowProcess } from '@/types/workflows';
import { ProcessesTab } from './workflows/ProcessesTab';
import { InboxTab } from './workflows/InboxTab';
import { InstancesTab } from './workflows/InstancesTab';
import styles from './AdminWorkflowsPage.module.css';

/**
 * Approval workflows — the institution's maker-checker console.
 *
 * This page replaced a screen that stored two numbers per workflow
 * ("participants", "levels") and called it configuration. What an institution
 * actually needs to govern is WHO must approve WHAT, in what order, under which
 * rules — and then to live inside that governance: see what is waiting on them,
 * decide it, and audit what was decided. Hence three tabs in the order an
 * administrator meets them:
 *
 *  - PROCESSES: the catalogue of governable actions and the law over each —
 *    platform default, customised, or ungoverned. Designing happens one level
 *    deeper, on the per-process designer route.
 *  - APPROVALS INBOX: the cases waiting on the signed-in person. Served by the
 *    member-scoped API, because being an approver is not an admin privilege.
 *  - ALL INSTANCES: every case, filterable, for audit and unblocking.
 *
 * The tab bar is a real WAI-ARIA tablist (roving tabindex, arrows move,
 * Home/End jump), the same pattern as AdminOrganisationPage.
 */

const NS = 'admin.workflows';

const TABS = ['processes', 'inbox', 'instances'] as const;

type TabId = (typeof TABS)[number];

export function AdminWorkflowsPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const [tab, setTab] = useState<TabId>('processes');
  const tabRefs = useRef(new Map<TabId, HTMLButtonElement>());

  const { state: processesState, reload: reloadProcesses } = useAsyncData<WorkflowProcess[]>(
    `wf-processes-${institutionId}`,
    () => workflowsApi.admin.processes(institutionId),
    !!institutionId,
  );

  const { state: inboxState, reload: reloadInbox } = useAsyncData<WorkflowInstance[]>(
    `wf-inbox-${institutionId}`,
    () => workflowsApi.member.inbox(institutionId),
    !!institutionId,
  );

  /** processKey → human title, so the inbox and register never show raw keys. */
  const processTitles = useMemo(() => {
    const map = new Map<string, string>();
    if (processesState.status === 'ready') {
      for (const process of processesState.data) map.set(process.key, process.title);
    }
    return map;
  }, [processesState]);

  const inboxCount = inboxState.status === 'ready' ? inboxState.data.length : null;

  const focusTab = (next: TabId) => {
    setTab(next);
    tabRefs.current.get(next)?.focus();
  };

  const moveFocus = (from: TabId, delta: number) => {
    const index = TABS.indexOf(from);
    focusTab(TABS[(index + delta + TABS.length) % TABS.length]);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.headerName}>{t(`${NS}.title`)}</h1>
          <p className={styles.headerSubtitle}>{t(`${NS}.subtitle`)}</p>
        </div>
      </header>

      <div role="tablist" aria-label={t(`${NS}.title`)} className={styles.tabList}>
        {TABS.map((id) => (
          <button
            key={id}
            ref={(node) => {
              if (node) tabRefs.current.set(id, node);
              else tabRefs.current.delete(id);
            }}
            type="button"
            role="tab"
            id={`wf-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`wf-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            className={tab === id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setTab(id)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                moveFocus(id, 1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                moveFocus(id, -1);
              } else if (event.key === 'Home') {
                event.preventDefault();
                focusTab(TABS[0]);
              } else if (event.key === 'End') {
                event.preventDefault();
                focusTab(TABS[TABS.length - 1]);
              }
            }}
          >
            {t(`${NS}.tabs.${id}`)}
            {/* The waiting count rides on the tab so an approver landing on the
                catalogue still sees there is work for them one tab over. */}
            {id === 'inbox' && inboxCount !== null && inboxCount > 0 && (
              <span className={styles.tabCount}>{inboxCount}</span>
            )}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`wf-panel-${tab}`}
        aria-labelledby={`wf-tab-${tab}`}
        tabIndex={0}
        className={styles.tabPanel}
      >
        {tab === 'processes' && (
          <ProcessesTab state={processesState} onRetry={reloadProcesses} />
        )}

        {tab === 'inbox' && (
          <InboxTab
            institutionId={institutionId}
            state={inboxState}
            onRetry={reloadInbox}
            onChanged={reloadInbox}
            processTitles={processTitles}
          />
        )}

        {tab === 'instances' && (
          <InstancesTab
            institutionId={institutionId}
            processes={processesState.status === 'ready' ? processesState.data : []}
            processTitles={processTitles}
          />
        )}
      </div>
    </div>
  );
}
